import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import {
    AlertCircle,
    ArrowLeft,
    Briefcase,
    Facebook,
    HelpCircle,
    Instagram,
    Megaphone,
    Music,
    Search,
    Ticket,
    Users,
} from 'lucide-react';

import Button from 'components/_UI/Button';
import Fieldset from 'components/_UI/Fieldset';
import Loader from 'components/_UI/Loader';
import colors from 'theme/colors';
import { FullyCentered } from 'styled/alignment/Center';

import {
    Actions,
    FieldStack,
    StepError,
    StepShell,
    StepSubtitle,
    StepTitle,
    SubmitButton,
} from '../partnerStyles';

/* O valor é o que a API recebe (`referral_source`); mudar o rótulo não
   quebra dados já enviados por quem parou o cadastro no meio do caminho. */
export const DISCOVERY_SOURCES = [
    { value: 'indicacao', label: 'Indicação de amigo ou parceiro', Icon: Users },
    { value: 'comercial', label: 'Contato do time comercial do Whizr', Icon: Briefcase },
    { value: 'evento_whizr', label: 'Já participei de um evento pelo Whizr', Icon: Ticket },
    { value: 'busca_google', label: 'Busca no Google', Icon: Search },
    { value: 'google_ads', label: 'Anúncio no Google', Icon: Megaphone },
    { value: 'instagram', label: 'Instagram', Icon: Instagram },
    { value: 'facebook', label: 'Facebook', Icon: Facebook },
    { value: 'tiktok', label: 'TikTok', Icon: Music },
    { value: 'outro', label: 'Outro', Icon: HelpCircle },
];

/**
 * Passo 4 — como a pessoa chegou até o Whizr.
 *
 * Pergunta nova, para dar ao time de marketing visibilidade sobre a origem de
 * cada parceiro (indicação, time comercial, mídia paga, redes, etc). Fica por
 * último porque nenhuma resposta aqui depende de nada coletado antes, e é
 * aqui que o cadastro é de fato enviado.
 */
export default function StepDiscovery({ defaultValues, onSubmit, onBack, submitting, submitError }) {
    const titleRef = useRef(null);

    const {
        register,
        handleSubmit,
        watch,
        resetField,
        clearErrors,
        formState: { errors },
    } = useForm({
        mode: 'onTouched',
        defaultValues: {
            referralSource: defaultValues?.referralSource || '',
            referralSourceOther: defaultValues?.referralSourceOther || '',
        },
    });

    const referralSource = watch('referralSource');
    const isOther = referralSource === 'outro';

    useEffect(() => {
        titleRef.current?.focus();
    }, []);

    useEffect(() => {
        // Trocar para outra opção some com o campo de texto; deixar o valor e o
        // erro para trás travaria o envio num campo que ninguém mais vê.
        if (!isOther) {
            resetField('referralSourceOther');
            clearErrors('referralSourceOther');
        }
    }, [isOther, resetField, clearErrors]);

    return (
        <StepShell>
            <StepTitle ref={titleRef} tabIndex={-1}>
                Como você conheceu o Whizr?
            </StepTitle>
            <StepSubtitle>
                Essa é a última pergunta — ajuda a gente a entender o que está funcionando.
            </StepSubtitle>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <SourceGroup role='radiogroup' aria-label='Como você conheceu o Whizr'>
                    {DISCOVERY_SOURCES.map(({ value, label, Icon }) => (
                        <SourceOption key={value} $selected={referralSource === value}>
                            <SourceInput
                                type='radio'
                                value={value}
                                {...register('referralSource', {
                                    required: 'Escolha uma opção para continuar.',
                                })}
                            />
                            <SourceIcon $selected={referralSource === value}>
                                <Icon size={16} strokeWidth={2} aria-hidden='true' />
                            </SourceIcon>
                            <SourceLabel>{label}</SourceLabel>
                        </SourceOption>
                    ))}
                </SourceGroup>

                {errors.referralSource && (
                    <GroupError role='alert'>
                        <AlertCircle size={14} aria-hidden='true' />
                        {errors.referralSource.message}
                    </GroupError>
                )}

                {isOther && (
                    <FieldStack>
                        <Fieldset
                            layout='squired'
                            label='Onde, exatamente?'
                            placeholder='Conte pra gente'
                            name='referralSourceOther'
                            register={register}
                            validations={{
                                required: 'Conte de onde você conheceu o Whizr.',
                                maxLength: { value: 255, message: 'Use no máximo 255 caracteres.' },
                            }}
                            error={errors.referralSourceOther?.message}
                        />
                    </FieldStack>
                )}

                {submitError && (
                    <StepError role='alert'>
                        <AlertCircle size={15} aria-hidden='true' />
                        {submitError}
                    </StepError>
                )}

                <Actions>
                    {submitting ? (
                        <FullyCentered>
                            <Loader />
                        </FullyCentered>
                    ) : (
                        <>
                            <SubmitButton>
                                Enviar cadastro
                            </SubmitButton>

                            <Button category='minimalDark' onClick={onBack}>
                                <ArrowLeft size={20} aria-hidden='true' /> &nbsp; Voltar
                            </Button>
                        </>
                    )}
                </Actions>
            </form>
        </StepShell>
    );
}

const SourceGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 22px;
`;

const SourceOption = styled.label`
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1.5px solid ${({ $selected }) => ($selected ? colors.secondaryPurple : '#DDDDE2')};
    background: ${({ $selected }) => ($selected ? `${colors.secondaryPurple}0D` : colors.neutralLight)};
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover {
        border-color: ${colors.secondaryPurple};
    }

    /* O input é um radio de verdade (só escondido), então setas do teclado,
       leitor de tela e validação do react-hook-form funcionam sem gambiarra. */
    &:focus-within {
        outline: 2px solid ${colors.secondaryPurple};
        outline-offset: 2px;
    }
`;

const SourceInput = styled.input`
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    margin: 0;
`;

const SourceIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: ${({ $selected }) => ($selected ? colors.secondaryPurple : '#EFEFF2')};
    color: ${({ $selected }) => ($selected ? '#fff' : colors.night)};
`;

const SourceLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const GroupError = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 10px 0 0;
    font-size: 12px;
    color: ${colors.danger};
`;
